// frontend/src/components/HeroBackground.js
import React, { useState, useEffect, useContext } from 'react';
import { Box } from '@mui/material';
import AuthContext from '../context/AuthContext';

const HeroBackground = ({ isMobile, bildDynamicStyle, settings }) => {
  const { getAssetUrl } = useContext(AuthContext);
  const { heroImage, heroImages, slideshowInterval = 6 } = settings || {};

  // Support both old heroImage (string) and new heroImages (array)
  const images = heroImages && Array.isArray(heroImages) && heroImages.length > 0
    ? heroImages
    : heroImage ? [heroImage] : [];

  const [currentIndex, setCurrentIndex] = useState(0);
  const [loadedIndices, setLoadedIndices] = useState(() => new Set([0]));

  // Progressive background loading: load slide 0 immediately, defer subsequent slides
  useEffect(() => {
    if (images.length <= 1) return;

    // Preload next slides shortly before they are needed, or after page load
    const deferTimer = setTimeout(() => {
      setLoadedIndices(prev => {
        const next = new Set(prev);
        for (let idx = 0; idx < images.length; idx++) {
          next.add(idx);
        }
        return next;
      });
    }, 2500);

    const timer = setInterval(() => {
      setCurrentIndex(prev => {
        const nextIndex = (prev + 1) % images.length;
        setLoadedIndices(loaded => new Set(loaded).add(nextIndex));
        return nextIndex;
      });
    }, slideshowInterval * 1000);

    return () => {
      clearTimeout(deferTimer);
      clearInterval(timer);
    };
  }, [images.length, slideshowInterval]);

  const targetWidth = isMobile ? 640 : 1600;

  return (
    <>
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 0,
          overflow: 'hidden',
          backgroundColor: '#1a1c19', // Fallback color
          ...bildDynamicStyle
        }}
      >
        {images.length > 0 ? (
          images.map((url, i) => {
            // Only render img element if it's the initial slide or has been queued for loading
            if (!loadedIndices.has(i)) return null;

            const optimizedUrl = getAssetUrl ? getAssetUrl(url, { width: targetWidth, quality: 72 }) : url;
            return (
              <img
                key={url}
                src={optimizedUrl}
                alt={`${settings?.organization?.stammName || 'Pfadfinder'} Hero Hintergrund`}
                fetchPriority={i === 0 ? 'high' : 'low'}
                loading={i === 0 ? 'eager' : 'lazy'}
                decoding={i === 0 ? 'sync' : 'async'}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  objectPosition: 'center',
                  opacity: i === currentIndex ? 1 : 0,
                  transition: 'opacity 1.2s ease-in-out',
                }}
              />
            );
          })
        ) : (
          <div className="bild" /> // CSS fallback
        )}
      </Box>

      <Box
        className="dark-overlay"
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0,0,0,0.5)',
          zIndex: 1
        }}
      />
    </>
  );
};

export default HeroBackground;
